import React from 'react';
import Header from './Header';
import Footer from './Footer';
import { useNavigate, useParams } from 'react-router-dom';
import { BASE_URL } from '../config/apiConfig';

function LoginFail() {
  const { orderId } = useParams(); // Get the order id from URL
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${BASE_URL}/sub-order-details/${orderId}`);
        if (!response.ok) throw new Error('Failed to fetch order details');
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderId]);

  const handleGoHome = () => navigate('/');
  const handleTryAgain = () => navigate('/cartSection');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-600 text-lg">Loading order status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-red-50 p-4">
        <p className="text-red-600 font-bold mb-4">Error: {error}</p>
        <button
          onClick={handleGoHome}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
        >
          🏠 Go Home
        </button>
      </div>
    );
  }

  const isFailed = data.payment_status !== 'paid';

  return (
    <>
      <Header />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 via-blue-50 to-gray-200 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center border border-gray-200">
          <div className="mb-6">
            <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center ${isFailed ? 'bg-red-500' : 'bg-green-500'}`}>
              {isFailed ? (
                <span className="text-white text-3xl">❌</span>
              ) : (
                <span className="text-white text-3xl">✅</span>
              )}
            </div>
          </div>

          <h1 className="text-3xl font-bold mb-2">
            {isFailed ? '😞 Order Failed' : '🎉 Order Successful'}
          </h1>

          <p className="text-gray-600 mb-6">
            {isFailed
              ? 'We were unable to process your payment.'
              : 'Your order has been successfully processed.'}
          </p>

          {/* Order Details */}
          <div className="bg-gradient-to-r from-gray-50 to-green-50 rounded-lg p-6 mb-6 border border-gray-100 text-left space-y-3">
            <div className="flex justify-between">
              <span className="font-medium">Order ID:</span>
              <span className="font-bold">{data.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Customer:</span>
              <span className="font-bold">{data.customer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Total Amount:</span>
              <span className={`font-bold ${isFailed ? 'text-red-600' : 'text-green-600'}`}>₹{data.total_amount}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Total Items:</span>
              <span className="font-bold">{data.total_items}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Payment Status:</span>
              <span className="font-bold capitalize">{data.payment_status}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {isFailed && (
              <button
                onClick={handleTryAgain}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg"
              >
                🔄 Try Again
              </button>
            )}
            <button
              onClick={handleGoHome}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 rounded-lg"
            >
              🏠 Go Home
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default LoginFail;
